/**
 * «Доставки»: дни наперёд и списки доставок и заборов (`GET /api/delivery/report`),
 * доставка одной сделки с журналом правок (`GET /api/delivery/{id}`), правка
 * (`POST /api/delivery/{id}/{start|end}` — пишет в боевой Bitrix и в договор CRM).
 * Спека `docs/specs/2026-10-08-delivery-design.md`.
 */
import { apiFetch, query } from "@/lib/api";
import type { Channel } from "@/lib/clientChats";
import type { CourierOrder } from "@/lib/dealDelivery";

/** start — привезти к началу проката, end — забрать в конце. */
export type Direction = "start" | "end";
/** active — везём, guard — отменил робот, cancelled — отменена, адрес остался, contract — только в договоре. */
export type RowKind = "active" | "guard" | "cancelled" | "contract";
export type StatusKind = "need" | "go" | "done" | "cancel" | "";
export type Gap = "address" | "time";

export type DeliveryRow = {
  deal_id: number;
  direction: Direction;
  day: string;
  city: string;
  stage: string;
  kind: RowKind;
  status: string;
  status_label: string;
  status_kind: StatusKind;
  address: string;
  time: string | null;
  time_salon: string | null;
  courier: string;
  courier_phone: string;
  comment: string;
  /** Заказ клиента со страницы брони: начало окна (конец — time), когда клиент последний раз менял, телефон для курьера, пожелание; tz — сдвиг салона к Москве. */
  window_start: string | null;
  client_at: string | null;
  phone: string;
  note: string;
  tz: number;
  gaps: Gap[];
  guard_status_label: string;
  guard_at: string | null;
  contract: { id: number | null; need: number | null; address: string };
  client: { name: string; phones: number[] };
  total: number;
  /** Переписка клиента в «Чатах»: чат сделки, иначе — его номера; где писали последним. */
  chat: { id: number; channel: Channel } | null;
};

export type DeliveryDay = { day: string; give: number; take: number; gaps: number; guards: number };
export type DeliveryStatus = { code: string; label: string; kind: StatusKind };

export type DeliveryReport = {
  period: { from: string; to: string };
  city: string;
  cities: string[];
  days: DeliveryDay[];
  give: DeliveryRow[];
  take: DeliveryRow[];
  statuses: Record<Direction, DeliveryStatus[]>;
  synced: string | null;
  can_edit: boolean;
};

export type DeliveryChange = {
  at: string;
  direction: Direction;
  /** Подпись поля для людей; `code` — поле как в журнале (`address`, `window`, `time_salon`…). */
  field: string;
  code: string;
  old: string;
  new: string;
  source: "panel" | "bitrix" | "robot" | "guard" | "client";
  source_label: string;
  who: string;
};

/** Состояние направления, как его хранит своя таблица: времена — `ГГГГ-ММ-ДД ЧЧ:ММ` или пусто. */
export type DeliveryNow = {
  status: string;
  address: string;
  time: string;
  time_salon: string;
  courier: string;
  courier_phone: string;
  comment: string;
  day: string | null;
};

export type DeliveryDeal = {
  deal_id: number;
  now: Partial<Record<Direction, DeliveryNow>>;
  changes: DeliveryChange[];
};

/** Что меняем: чего нет — не трогаем, пустая строка — стереть. */
export type DeliveryEdit = {
  need?: boolean;
  status?: string;
  address?: string;
  time?: string;
  time_salon?: string;
  courier?: string;
  courier_phone?: string;
  comment?: string;
  restore?: boolean;
};

export type ContractOutcome = "ok" | "same" | "no_contract" | "no_access" | "failed";
export type DeliveryResult = {
  deal_id: number;
  direction: Direction;
  /** pending — Bitrix не ответил, правку дошлёт очередь записей. */
  bitrix: "ok" | "same" | "pending";
  contract: ContractOutcome;
  changes: number;
};

export const loadDelivery = (from: string, to: string, city: string) =>
  apiFetch<DeliveryReport>(`/api/delivery/report${query({ from, to, city })}`);

export const loadDeliveryDeal = (deal: number) => apiFetch<DeliveryDeal>(`/api/delivery/${deal}`);

/** Вкладка «Заявки»: заявки на курьера из prokat; времена — местные салона, `date` — МСК. */
export type JournalOrder = CourierOrder & {
  deal_id: number;
  city: string;
  client: { name: string; phones: number[] };
  window: [string | null, string | null];
  address: string;
  chat: { id: number; channel: Channel } | null;
};
export type OrdersJournal = {
  period: { from: string; to: string };
  orders: JournalOrder[];
  totals: {
    count: number;
    live: number;
    delivered: number;
    cancelled: number;
    price: number;
    client_price: number;
    providers: Record<"yandex" | "dostavista", number>;
  };
};

export const loadDeliveryOrders = (from: string, to: string) =>
  apiFetch<OrdersJournal>(`/api/delivery/orders${query({ from, to })}`);

export const saveDelivery = (deal: number, direction: Direction, body: DeliveryEdit) =>
  apiFetch<DeliveryResult>(`/api/delivery/${deal}/${direction}`, {
    method: "POST",
    body: JSON.stringify(body),
  });

export const CONTRACT_TEXT: Record<ContractOutcome, string> = {
  ok: "договор: записан",
  same: "договор: без изменений",
  no_contract: "договора у сделки нет",
  no_access: "договор НЕ записан — учётке ai-write не выданы права",
  failed: "договор НЕ записан — CRM не приняла, повторите",
};

/** Договор не записан, хотя должен был: показываем красным. */
export const contractFailed = (o: ContractOutcome) => o === "no_access" || o === "failed";

const pad = (n: number) => String(n).padStart(2, "0");
export const iso = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
export const addDays = (day: string, n: number) => {
  const d = new Date(Number(day.slice(0, 4)), Number(day.slice(5, 7)) - 1, Number(day.slice(8, 10)));
  d.setDate(d.getDate() + n);
  return iso(d);
};
const WEEKDAYS = ["вс", "пн", "вт", "ср", "чт", "пт", "сб"];
export const weekday = (day: string) =>
  WEEKDAYS[new Date(Number(day.slice(0, 4)), Number(day.slice(5, 7)) - 1, Number(day.slice(8, 10))).getDay()]!;
/** `2026-10-09` → «09.10». */
export const dm = (day: string) => `${day.slice(8, 10)}.${day.slice(5, 7)}`;
/** `2026-10-09T12:00` → «12:00», другой день — «10.10 12:00». */
export const timeOf = (at: string | null, day: string) =>
  !at ? "" : at.slice(0, 10) === day ? at.slice(11, 16) : `${dm(at)} ${at.slice(11, 16)}`;
/** 79161234567 → «+7 916 123-45-67». */
export const phone = (n: number) => {
  const s = String(n);
  return s.length === 11 ? `+${s[0]} ${s.slice(1, 4)} ${s.slice(4, 7)}-${s.slice(7, 9)}-${s.slice(9)}` : `+${s}`;
};
